import React, { useState } from 'react';

import { ApplicationRoutes } from 'common/constants';
import Loader from 'components/navigation/Loader';
import { initialiseFilters } from 'features/resultFiltering/resultFilteringSlice';
import ResultSelection from 'features/searchMolecules/ResultSelection';
import {
  getMembersFromSelectedResult,
  getSelectedResult,
  getSelectedResultMemberCount,
} from 'features/searchMolecules/searchMoleculesSlice';
import { getCurrentCart } from 'features/shoppingCart/shoppingCartSlice';
import { changeWorkingQueryMolFile } from 'features/workingQuery/workingQuerySlice';
import { useCalculations, useThunkDispatch } from 'hooks';
import isEmpty from 'lodash/isEmpty';
import { usePrevious } from 'react-delta';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import FragnetService, { Calc } from 'services/FragnetService';

import { Container, Divider, Grow, Paper, Tab, Tabs, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import Summary from '../../../components/Summary';
import Filtering from '../../resultFiltering/Filtering';
import FilteringToggleButton from '../../resultFiltering/FilteringToggleButton';
import ResultGroupContentView from './ResultGroupContentView';
import ResultGroupDetailView from './ResultGroupDetailView';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tabs: {
      borderRight: `2px solid ${theme.palette.divider}`,
    },
    tab: {
      backgroundColor: theme.palette.grey[100],
      borderTopLeftRadius: '.5rem',
      borderBottomLeftRadius: '.5rem',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    resultsViewContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr 7fr',
      gridAutoFlow: 'column',
      gridAutoColumns: '12rem',
      gridColumnGap: theme.spacing(4),
      marginRight: 0,
    },
    sidebar: {
      display: 'flex',
      flexDirection: 'column',
      '& > :not(:last-child)': {
        marginBottom: '1rem',
      },
      '& > :last-child': {
        flexBasis: '50vh',
        flexGrow: 1,
      },
    },
    main: {
      display: 'flex',
      flexDirection: 'row',
    },
  }),
);

const ResultsView = () => {
  const currentCart = useSelector(getCurrentCart);
  const selectedResult = useSelector(getSelectedResult);
  const selectedResultMembers = useSelector(getMembersFromSelectedResult);
  const selectedResultMemberCount = useSelector(getSelectedResultMemberCount);
  const history = useHistory();

  const dispatch = useThunkDispatch();

  const [selectedGroupIdx, setSelectedGroupIdx] = useState(0);
  const resultGroups = (selectedResult ?? {}).groups ?? [];
  const selectedGroupData =
    resultGroups.length > selectedGroupIdx ? resultGroups[selectedGroupIdx] : undefined;

  const handleTabChange = (_: object, newValue: number) => {
    setSelectedGroupIdx(newValue);
  };

  const { t } = useTranslation(['common', 'results']);
  const classes = useStyles();

  const calculations = useCalculations();
  const calcsLookup: { [key: string]: string } = {};
  calculations.forEach((calc: Calc) => {
    calcsLookup[calc.name] = calc.description;
  });

  // TODO: Should check over all groups in case a member is missing a calc
  const activeCalcs = Object.keys(
    selectedResult?.groups[selectedGroupIdx].members[0].calculations ?? {},
  );

  activeCalcs.filter((name) => {
    if (calculations.find((calc) => calc.name === name)) {
      return true;
    }
    return false;
  });

  // Track whether the moFile changes - implies the selected result has changed
  const prevMolFile = usePrevious(selectedResult?.molFile);
  // Update the filtering when the selected result changes
  if (prevMolFile !== selectedResult?.molFile) {
    dispatch(initialiseFilters({ activeCalcs, selectedResultMembers }));
  }

  const handleGroupAsNewQuery = (smiles: string) => {
    FragnetService.getMolFileFromSmiles(smiles).then((result) => {
      dispatch(changeWorkingQueryMolFile({ molFile: result.data, shouldJSMEUpdate: true }));
      history.push(ApplicationRoutes.Query);
    });
  };

  const [filtersVisible, setFiltersVisible] = useState<boolean>(false);

  if (isEmpty(calcsLookup)) return <Loader open={true} reason={t('calcs_loading')} />;

  return (
    <>
      <ResultSelection />
      <main className={classes.main}>
        <Container maxWidth="xl">
          <div className={classes.resultsViewContainer}>
            <aside className={classes.sidebar}>
              {selectedResult && (
                <Summary
                  selectedResultMemberCount={selectedResultMemberCount}
                  selectedResult={selectedResult}
                />
              )}
              <Divider variant="middle" />
              <Typography variant="h5">{t('results:header_groups')}</Typography>
              <Tabs
                orientation="vertical"
                variant="scrollable"
                value={selectedGroupIdx}
                onChange={handleTabChange}
                className={classes.tabs}
                indicatorColor="secondary"
                scrollButtons="off"
              >
                {resultGroups.map((group, index) => (
                  // component property of Tab has to accept provided reference, that's why we are using React.forwardRef to define component
                  <Tab
                    key={`tab-${index}`}
                    component={React.forwardRef<HTMLDivElement>((props, ref) => (
                      <div
                        ref={ref}
                        {...props}
                        className={index === selectedGroupIdx ? classes.tab : ''}
                      >
                        <ResultGroupDetailView
                          group={group}
                          refmol={selectedResult!.refmol}
                          index={index}
                          isSelected={selectedGroupIdx === index}
                        />
                      </div>
                    ))}
                  />
                ))}
              </Tabs>
            </aside>

            <Grow in={true}>
              <Paper square elevation={5} component="main">
                {selectedGroupData && (
                  <ResultGroupContentView
                    groupMembers={selectedGroupData.members}
                    refmol={selectedResult!.refmol}
                    calcs={calcsLookup}
                    cartItems={currentCart.items}
                    loadAsQuery={handleGroupAsNewQuery}
                    resultGroups={resultGroups}
                  />
                )}
              </Paper>
            </Grow>

            <Grow in={filtersVisible} unmountOnExit>
              <Filtering
                closeFilterPanel={() => setFiltersVisible(false)}
                activeCalcs={activeCalcs}
                calcsLookup={calcsLookup}
                selectedResultMembers={selectedResultMembers}
                selectedResultChanged={prevMolFile !== selectedResult?.molFile}
              />
            </Grow>
          </div>
        </Container>
        {!filtersVisible && (
          <FilteringToggleButton
            filtersVisible={filtersVisible}
            setFiltersVisible={setFiltersVisible}
            calcsLookup={calcsLookup}
          />
        )}
      </main>
    </>
  );
};

export default ResultsView;
